import { Navigate, Route, Routes } from "react-router-dom";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { RequireAuth } from "@/components/RequireAuth";
import { LazyGate, lazyRoute } from "@/vendor/chat-shell";
import { useLocation } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import NotFoundPage from "@/pages/NotFoundPage";
const AuthCallbackPage = lazyRoute(() => import("@/pages/AuthCallbackPage"));
const WannlabsSsoPage = lazyRoute(() => import("@/pages/WannlabsSsoPage"));
const SignupPage = lazyRoute(() => import("@/pages/SignupPage"));
const OnboardingPage = lazyRoute(() => import("@/pages/OnboardingPage"));
const SettingsPage = lazyRoute(() => import("@/pages/SettingsPage"));
const VoiceStudioPage = lazyRoute(() => import("@/pages/voice/VoiceStudioPage"));
const ConnectionsPage = lazyRoute(() => import("@/pages/ConnectionsPage"));
const UsagePage = lazyRoute(() => import("@/pages/UsagePage"));
const PricingPage = lazyRoute(() => import("@/pages/PricingPage"));
const PrivacyPage = lazyRoute(() => import("@/pages/PrivacyPage"));
const TermsPage = lazyRoute(() => import("@/pages/TermsPage"));
const ChatPage = lazyRoute(() => import("@/pages/ChatPage"));
const DashboardPage = lazyRoute(() => import("@/pages/DashboardPage"));
const DesktopReturnPage = lazyRoute(() => import("@/pages/DesktopReturnPage"));
const HelpersPage = lazyRoute(() => import("@/pages/helpers/HelpersPage"));
const NewHelperPage = lazyRoute(() => import("@/pages/helpers/NewHelperPage"));
const HelperDetailPage = lazyRoute(() => import("@/pages/helpers/HelperDetailPage"));
const HelperRunThreadPage = lazyRoute(() => import("@/pages/helpers/HelperRunThreadPage"));
const HelperAutomationPage = lazyRoute(() => import("@/pages/helpers/HelperAutomationPage"));
const PromptTemplatesPage = lazyRoute(() => import("@/pages/helpers/PromptTemplatesPage"));
const PipelineFollowUpsPage = lazyRoute(() => import("@/pages/helpers/PipelineFollowUpsPage"));
const ConnectorsPage = lazyRoute(() => import("@/pages/ConnectorsPage"));
const ApprovalPage = lazyRoute(() => import("@/pages/approvals/ApprovalPage"));
const GrantAccessPage = lazyRoute(() => import("@/pages/GrantAccessPage"));
const TwilioSetupPage = lazyRoute(() => import("@/pages/setup/TwilioSetupPage"));
const AdminUsersPage = lazyRoute(() => import("@/pages/AdminUsersPage"));
const FileReadinessPage = lazyRoute(() => import("@/pages/FileReadinessPage"));
const CloudPage = lazyRoute(() => import("@/pages/CloudPage"));
const ViewAsOverviewPage = lazyRoute(() => import("@/pages/ViewAsOverviewPage"));

/** Skeleton shape for the route being loaded (chat gets the message column, lists get rows). */
function layoutFor(path: string) {
  if (path.startsWith("/chat")) return "chat" as const;
  if (/^\/(helpers|cloud|connections|connectors|settings\/admin|usage)/.test(path)) return "list" as const;
  return "page" as const;
}

export default function App() {
  const { pathname } = useLocation();
  return (
    <>
    <ImpersonationBanner />
    <LazyGate layout={layoutFor(pathname)} label="Loading Prime…">
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/auth/wannlabs" element={<WannlabsSsoPage />} />
      <Route path="/auth/desktop-return" element={<DesktopReturnPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/approvals/:token" element={<ApprovalPage />} />
      <Route path="/grant/:token" element={<GrantAccessPage />} />
      <Route path="/setup/twilio" element={<TwilioSetupPage />} />
      <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
      <Route path="/helpers" element={<Navigate to="/chat" replace />} />
      <Route path="/helpers/new" element={<Navigate to="/chat" replace />} />
      <Route
        path="/helpers/automation"
        element={
          <RequireAuth>
            <HelperAutomationPage />
          </RequireAuth>
        }
      />
      <Route
        path="/helpers/prompt-templates"
        element={
          <RequireAuth>
            <PromptTemplatesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/helpers/pipeline-follow-ups"
        element={
          <RequireAuth>
            <PipelineFollowUpsPage />
          </RequireAuth>
        }
      />
      <Route path="/helpers/:id" element={<RequireAuth><HelperDetailPage /></RequireAuth>} />
      <Route
        path="/helpers/:id/runs/:runId"
        element={
          <RequireAuth>
            <HelperRunThreadPage />
          </RequireAuth>
        }
      />
      <Route path="/chat" element={<RequireAuth><ChatPage /></RequireAuth>} />
      <Route path="/cloud" element={<RequireAuth><CloudPage /></RequireAuth>} />
      <Route path="/file-readiness" element={<RequireAuth><FileReadinessPage /></RequireAuth>} />
      <Route path="/usage" element={<RequireAuth><UsagePage /></RequireAuth>} />
      <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
      <Route path="/voice-studio" element={<RequireAuth><VoiceStudioPage /></RequireAuth>} />
      <Route path="/connections" element={<RequireAuth><ConnectionsPage /></RequireAuth>} />
      <Route path="/settings/admin" element={<RequireAuth><AdminUsersPage /></RequireAuth>} />
      <Route path="/view-as/overview" element={<RequireAuth><ViewAsOverviewPage /></RequireAuth>} />
      <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />
      <Route path="/connectors" element={<RequireAuth><ConnectorsPage /></RequireAuth>} />
      <Route
        path="/use-with-claude"
        element={<Navigate to="/settings" replace />}
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </LazyGate>
    </>
  );
}
